import type { ColumnDef } from "@tanstack/react-table";
import DataTable, { type Cell } from "../components/table/ServerSideTable";
import { useEarmoldsPaginated } from "../hooks/earmolds.hooks";
import type { Earmold } from "../types/earmold.types";
import { useMemo, useRef, useState } from "react";
import ProductBadge from "../components/ProductBadge";
import SimpleModal from "../components/modals/SimpleModal";
import { useDisclosure } from "@chakra-ui/react";
import EarmoldForm, {
	type EarmoldFormHandler,
} from "../components/forms/EarmoldForm";
import { useMutation, useQueryClient } from "react-query";
import { updateEarmold } from "../mutations/earmold.mutations";
import { Text } from "@chakra-ui/react";
import { useFile } from "../hooks/file.hooks";

const EarmoldImage = ({ fileId }: { fileId?: string | number }) => {
	const { data: file } = useFile({ id: fileId });

	return (
		<img
			src={file ? file.url : "https://placehold.co/200x200"}
			alt="Embout"
			className="aspect-square w-full h-full object-cover"
		/>
	);
};

const EarmoldTab = () => {
	const productModal = useDisclosure();
	const productModalRef = useRef<EarmoldFormHandler>(null);
	const [selectedEarmold, setSelectedEarmold] = useState<Earmold | null>(null);
	const columns = useMemo<ColumnDef<Earmold>[]>(() => {
		return [
			{
				accessorFn: (row) => row.label,
				cell: ({ row }) => (
					<div className="flex flex-row gap-3 items-center">
						<div className="w-10 h-10 rounded-xl overflow-hidden">
							<EarmoldImage fileId={row.original.fileId ?? undefined} />
						</div>
						<Text fontSize="12px" fontWeight={500}>
							{row.original.label}
						</Text>
					</div>
				),
				id: "name",
				header: "Embouts",
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <ProductBadge status={row.status}></ProductBadge>,
				id: "status",
				header: "Statut",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		];
	}, []);
	const queryClient = useQueryClient();
	const { mutate: mutateUpdateEarmold } = useMutation(updateEarmold, {
		onSuccess: () => {
			queryClient.invalidateQueries("earmolds-paginated");
		},
	});
	const handleUpdate = async () => {
		if (productModalRef.current) {
			const valid = await productModalRef.current.valid();
			if (valid) {
				const earmold = productModalRef.current.values();
				mutateUpdateEarmold({
					id: selectedEarmold?.id,
					earmold,
				});
				productModal.onClose();
			}
		}
	};

	return (
		<>
			<DataTable
				columns={columns}
				paginatedQuery={useEarmoldsPaginated}
				w={["auto", "12rem"]}
				flexGrow={[1, 0]}
				cellLink={(cell: Cell) => {
					if (cell.id.includes("name")) {
						return `/products/earmolds/${cell.row.id}`;
					}
				}}
				handleCellClick={(cell: Cell) => {
					if (cell.id.includes("status")) {
						setSelectedEarmold(cell.row);
						productModal.onOpen();
					}
				}}
			></DataTable>
			<SimpleModal
				isOpen={productModal.isOpen}
				onClose={productModal.onClose}
				title="Modifier un produit"
				onClick={handleUpdate}
				isUpdate
			>
				<EarmoldForm
					ref={productModalRef}
					value={selectedEarmold ?? undefined}
				/>
			</SimpleModal>
		</>
	);
};

export default EarmoldTab;
